import React, { FC, useId } from 'react';

import LocationInput from '@rover/components/src/formFields/LocationInput';
import { TemporaryAddressProvider } from '@rover/components/src/formFields/LocationInput/hooks/useTemporaryAddress';
import {
  actions as locationInputActions,
  effects as locationInputEffects,
  selectors as locationInputSelectors,
} from '@rover/components/src/formFields/LocationInput/LocationInput.duck';
import Connect from '@rover/react-lib/src/components/utils/Redux/Connect';
import { selectors as frontendConfigurationSelectors } from '@rover/react-lib/src/redux/ducks/frontendConfiguration.duck';
import type { UserInformationAddress } from '@rover/rsdk/src/apiClient/latest/model/userInformationAddress';

import type { ContainerProps } from './container.constants';

const effects = {
  fetchGeocodedLatLng: locationInputEffects.fetchLocationDeviceOrGeocode,
  loadOptions: locationInputEffects.fetchLocationAutocomplete,
};

const actions = {
  dismissErrorMessage: locationInputActions.dismissErrorMessage,
  clearOptions: locationInputActions.clearLocationAutocompleteOptions,
};

const selector = (state, id, includeCurrentLocation) => ({
  // @ts-expect-error
  geocodedLatLng: locationInputSelectors.getLocationGeocodeResponse(state, id),
  // @ts-expect-error
  geocodeResultsAreLoading: locationInputSelectors.getLocationGeocodeResultsAreLoading(state, id),
  // @ts-expect-error
  loading: locationInputSelectors.getIsLoading(state, id),
  // @ts-expect-error
  options: locationInputSelectors.getLocationAutocompleteOptions(state, id, includeCurrentLocation),
  region: frontendConfigurationSelectors.getCcTld(state),
  // @ts-expect-error
  selectedOption: locationInputSelectors.getSelectedOption(state, id),
  // @ts-expect-error
  errorMessage: locationInputSelectors.getErrorMessage(state, id),
});

type Props = ContainerProps<
  typeof LocationInput,
  // Note: Specifying the Object type for onChange cascades into several complex typescript errors
  { onChange: (Object) => void; onClear?: () => void }
> & {
  inputId?: string;
  countryCode?: string | null | undefined;
  validationType: 'popover' | 'inline';
  validationError?: string | null | undefined;
  includeCurrentLocation?: boolean;
  includeHomeAddress?: boolean;
  forceUseHomeAddress?: boolean;
  initialTemporaryAddress?: Partial<UserInformationAddress> | null;
};

const LocationInputContainer: FC<Props> = (props) => {
  const autoGeneratedId = useId();
  const {
    inputId = autoGeneratedId,
    includeCurrentLocation = true,
    countryCode,
    initialTemporaryAddress,
  } = props;

  return (
    <Connect
      effects={effects}
      actions={actions}
      selector={(state) => selector(state, inputId, includeCurrentLocation)}
    >
      {/* removing dispatch from connectedProps is a fine thing to do here */}
      {/* eslint-disable-next-line @typescript-eslint/no-unused-vars */}
      {({ dispatch, ...connectedProps }) => (
        <TemporaryAddressProvider initialAddress={initialTemporaryAddress}>
          <LocationInput
            {...connectedProps}
            {...props}
            inputId={inputId}
            countryCode={countryCode}
          />
        </TemporaryAddressProvider>
      )}
    </Connect>
  );
};

export default LocationInputContainer;
